/**
 * O cone de incerteza do sistema selecionado (o da ficha aberta), no mapa
 * (pedido do usuário, 09/10/2026; desenho escolhido entre variantes por três
 * agentes julgadores): a área por onde o CENTRO do sistema deve passar até
 * +3 h, calculada em `cone.ts` a partir da previsão do pyForTraCC (o contorno
 * transladado, `registro.ts`) e da variação do movimento do próprio sistema.
 * Substitui as linhas do caminho passado e da projeção, que saíram.
 *
 * O desenho, em ciano (a cor da previsão; o violeta é do selecionado):
 * - faixas de hora em hora que NÃO se somam (0–1 h mais forte, 2–3 h mais
 *   fraca: a área maior não ganha destaque só por ser maior);
 * - a borda tracejada sobre um casco escuro (lê-se sobre nuvem e floresta);
 * - uma TRAVESSA em cada antecedência, de lado a lado do cone — as marcas dos
 *   horários, contínuas nas horas cheias e tracejadas nas meias —, com o
 *   horário escrito na ponta, fora do cone (no celular, só o último e o da
 *   tela); a antecedência desenhada no mapa ganha a sua elipse inteira, o
 *   ponto maior e "17:10 · +2 h";
 * - a origem (onde o centro está agora) num ponto escuro de borda ciano.
 *
 * Também com o player tocando (pedido do usuário, 09/10/2026): o cone
 * acompanha o sistema quadro a quadro. Os arquivos da previsão não são
 * baixados na reprodução, então ali os centros saem do vetor da previsão
 * mais perto no tempo que está na memória (`registro.ts`
 * `previsaoPeloVetor`): o mesmo movimento do cone parado, que usa o arquivo
 * do quadro — o do contorno previsto desenhado. Sem a trajetória do quadro
 * (âncora) não há origem: não há cone; com velocidade implausível (acima de
 * 200 km/h, erro de associação do rastreio), também não. Sistema
 * novo (menos de 30 min de trajetória): o cone só vai até +1 h. Desligado o
 * satélite, some. Os horários são de Brasília.
 *
 * O botão "Cone" da ficha (compacta e completa) liga e desliga o cone; a
 * escolha fica lembrada (`coneLigado.ts`).
 *
 * É mais uma camada do mapa: entra na exportação como o destaque da seleção,
 * e a ficha desvia dele (`extensaoDoCone`, `layer.ts` `areaDoSistema`).
 */
import type { Coordinate } from 'ol/coordinate';
import { createEmpty, extend, isEmpty, type Extent } from 'ol/extent';
import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { subscribeSelector } from '../lib/store';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { useLoopStore } from '../loop/store';
import { Z } from '../map/zIndex';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { anelDoCone, dispersaoDoTrajeto, elipsesDoCone, horizonteDoCone, pontosDaElipse, travessa } from './cone';
import { ancoraDoSistema, previsaoDoSistema, previsaoPeloVetor, VELOCIDADE_MAXIMA_KMH, type Ancora, type CentrosPrevistos } from './registro';
import { useConeStore } from './coneLigado';
import { sistemaDaSelecao } from './selecao';
import { previsaoDesenhada, previsaoDoSelecionado, useTrackingStore } from './store';
import { ALFA_DAS_FAIXAS_DO_CONE, comAlfa, COR_PREVISAO } from './tracos';
import { rotuloAntecedencia } from './tempo';

/** Mesma consulta do `useIsMobile`: o celular mostra menos horários. */
const CELULAR = '(max-width: 767px), (max-height: 500px) and (pointer: coarse)';

/**
 * As antecedências que levam o horário escrito: as de hora cheia (+1 h, +2 h,
 * +3 h) e a desenhada no mapa (`naTela`, 0 = nenhuma). No celular, só a última
 * e a desenhada: a tela é estreita e o sistema, pequeno nela.
 */
export function rotulosDaTrajetoria(hs: readonly number[], naTela: number, compacto: boolean): number[] {
  if (!hs.length) return [];
  if (!compacto) return hs.filter((h) => (h > 0 && h % 60 === 0) || (naTela > 0 && h === naTela));
  const ultima = hs[hs.length - 1];
  return hs.filter((h) => h === ultima || (naTela > 0 && h === naTela));
}

export type Papel = 'faixa' | 'borda' | 'marca' | 'elipse' | 'origem' | 'ponto' | 'rotulo';

export interface EntradaDaTrajetoria {
  ancora: Pick<Ancora, 'trajeto' | 'dentro'> | null;
  /** Os centros previstos: do arquivo da previsão, ou pelo vetor do próprio quadro (`previsaoPeloVetor`). */
  previsao: CentrosPrevistos | null;
  /** O cone entra (há sistema selecionado e satélite). */
  prevista: boolean;
  /** Antecedência desenhada no mapa (min), 0 = nenhuma. */
  naTela: number;
  compacto: boolean;
}

/** As horas que fecham faixa (+1 h, +2 h, +3 h) até a última antecedência, que sempre fecha a de fora. */
function limitesDasFaixas(hs: readonly number[]): number[] {
  const ultima = hs[hs.length - 1];
  return [...new Set([...hs.filter((h) => h % 60 === 0 && h < ultima), ultima])].sort((a, b) => a - b);
}

/**
 * As feições do cone: as faixas (anéis: cada uma sem a anterior), a borda, a
 * travessa e o ponto de cada antecedência, o rótulo dos horários escritos, a
 * elipse da antecedência no mapa e a origem. Vazio sem âncora, sem previsão ou
 * com o loop tocando.
 */
export function feicoesDaTrajetoria(e: EntradaDaTrajetoria): Feature[] {
  const out: Feature[] = [];
  if (!e.prevista || !e.ancora || !e.previsao) return out;
  const trajeto = e.ancora.trajeto ?? null;
  const horizonte = horizonteDoCone(
    e.previsao.passos.map((p) => p.h),
    trajeto,
  );
  const passos = e.previsao.passos.filter((p) => p.h <= horizonte);
  if (!passos.length) return out;
  const origem = (trajeto?.[trajeto.length - 1] as [number, number] | undefined) ?? e.ancora.dentro;
  const elipses = elipsesDoCone(origem, passos, dispersaoDoTrajeto(trajeto));

  // Faixas como anéis: a de +1 h inteira, a de +2 h sem a de +1 h (o furo vai no sentido horário)...
  let anterior: Coordinate[] | null = null;
  limitesDasFaixas(passos.map((p) => p.h)).forEach((ate, i) => {
    const anel = anelDoCone(elipses, ate);
    out.push(new Feature({ geometry: new Polygon(anterior ? [anel, [...anterior].reverse()] : [anel]), papel: 'faixa' satisfies Papel, nivel: i }));
    anterior = anel;
  });
  if (anterior) out.push(new Feature({ geometry: new LineString(anterior), papel: 'borda' satisfies Papel }));

  const comHorario = new Set(rotulosDaTrajetoria(passos.map((p) => p.h), e.naTela, e.compacto));
  const t = e.previsao.t;
  for (const el of elipses) {
    if (el.h === 0) continue;
    const naTela = el.h === e.naTela;
    const [esq, dir] = travessa(el);
    out.push(new Feature({ geometry: new LineString([esq, dir]), papel: 'marca' satisfies Papel, cheia: el.h % 60 === 0, naTela }));
    if (naTela) out.push(new Feature({ geometry: new Polygon([[...pontosDaElipse(el), pontosDaElipse(el)[0]]]), papel: 'elipse' satisfies Papel }));
    out.push(new Feature({ geometry: new Point(el.centro), papel: 'ponto' satisfies Papel, h: el.h, cheia: el.h % 60 === 0, naTela }));
    if (comHorario.has(el.h)) {
      // O horário na ponta da travessa do lado de cima da tela (ou da esquerda), fora do cone.
      const [ponta, lado] = esq[1] > dir[1] || (esq[1] === dir[1] && esq[0] < dir[0]) ? [esq, sub(esq, el.centro)] : [dir, sub(dir, el.centro)];
      const hora = formatTimeBrt(t + el.h * MINUTE_MS);
      out.push(
        new Feature({
          geometry: new Point(ponta),
          papel: 'rotulo' satisfies Papel,
          h: el.h,
          naTela,
          rotulo: naTela ? `${hora} · ${rotuloAntecedencia(el.h, { unidade: true })}` : hora,
          lado: unitario(lado),
        }),
      );
    }
  }
  out.push(new Feature({ geometry: new Point(origem), papel: 'origem' satisfies Papel }));
  return out;
}

const sub = (a: Coordinate, b: Coordinate): [number, number] => [a[0] - b[0], a[1] - b[1]];
function unitario(v: [number, number]): [number, number] {
  const n = Math.hypot(v[0], v[1]);
  return n > 0 ? [v[0] / n, v[1] / n] : [0, 1];
}

/** A normal (unitária, no sentido norte) à direção (dx, dy) do caminho, em coordenadas do mapa. */
export function ladoDoRotulo(dx: number, dy: number): [number, number] {
  const n = Math.hypot(dx, dy);
  if (!(n > 0)) return [0, 1];
  let x = -dy / n;
  let y = dx / n;
  if (y < 0 || (y === 0 && x < 0)) {
    x = -x;
    y = -y;
  }
  return [x, y];
}

// --- Estilos (todos prontos: o cone é redesenhado a cada quadro de pan e zoom) ---

const CASCO = 'rgba(8, 10, 16, 0.55)';
const ESCURO = '#0b0e14';
const ciano = (alfa: number) => comAlfa(COR_PREVISAO, alfa);

const FAIXAS = ALFA_DAS_FAIXAS_DO_CONE.map((a) => new Style({ fill: new Fill({ color: ciano(a) }) }));
const BORDA = [
  new Style({ stroke: new Stroke({ color: CASCO, width: 3.5, lineCap: 'round', lineJoin: 'round' }) }),
  new Style({ stroke: new Stroke({ color: ciano(1), width: 1.6, lineDash: [6, 4], lineCap: 'round', lineJoin: 'round' }) }),
];
const MARCA_CHEIA = [
  new Style({ stroke: new Stroke({ color: CASCO, width: 2.6, lineCap: 'round' }) }),
  new Style({ stroke: new Stroke({ color: ciano(0.85), width: 1.1, lineCap: 'round' }) }),
];
const MARCA_MEIA = [new Style({ stroke: new Stroke({ color: ciano(0.55), width: 1, lineDash: [2, 4], lineCap: 'round' }) })];
const ELIPSE = [
  new Style({ stroke: new Stroke({ color: CASCO, width: 3.4 }) }),
  new Style({ stroke: new Stroke({ color: ciano(0.95), width: 1.8 }) }),
];
const circulo = (raio: number, preenchimento: string, contorno: string, largura: number) =>
  new Style({
    image: new CircleStyle({ radius: raio, declutterMode: 'none', fill: new Fill({ color: preenchimento }), stroke: new Stroke({ color: contorno, width: largura }) }),
  });
const ORIGEM = [circulo(6.2, CASCO, CASCO, 1), circulo(5, ESCURO, ciano(1), 2)];
const PONTO_CHEIO = circulo(3.5, ciano(1), ESCURO, 1.2);
const PONTO_MEIO = circulo(2, ciano(0.75), ESCURO, 0.8);
const PONTO_NA_TELA = [circulo(7.2, CASCO, CASCO, 1), circulo(6, '#ffffff', ciano(1), 2.5)];

/** Distância (px) entre a ponta da travessa e o horário. */
const DISTANCIA_DO_ROTULO = 7;
const estilosDoRotulo = new Map<string, Style>();
function estiloDoRotulo(f: FeatureLike): Style {
  const naTela = f.get('naTela') === true;
  const rotulo = (f.get('rotulo') as string | null) ?? '';
  const [nx, ny] = (f.get('lado') as [number, number] | undefined) ?? [0, 1];
  const chave = `${naTela ? 1 : 0}|${rotulo}|${nx.toFixed(1)}|${ny.toFixed(1)}`;
  let s = estilosDoRotulo.get(chave);
  if (!s) {
    s = new Style({
      text: new Text({
        text: rotulo,
        font: `${naTela ? 700 : 600} ${naTela ? 12 : 11}px ui-sans-serif, system-ui, sans-serif`,
        fill: new Fill({ color: '#f4eeff' }),
        stroke: new Stroke({ color: 'rgba(8, 10, 16, 0.9)', width: 3.5 }),
        // A tela tem y para baixo; o mapa, para o norte.
        offsetX: nx * DISTANCIA_DO_ROTULO,
        offsetY: -ny * DISTANCIA_DO_ROTULO,
        textAlign: nx > 0.35 ? 'left' : nx < -0.35 ? 'right' : 'center',
        textBaseline: ny > 0.35 ? 'bottom' : ny < -0.35 ? 'top' : 'middle',
      }),
    });
    if (estilosDoRotulo.size > 200) estilosDoRotulo.clear();
    estilosDoRotulo.set(chave, s);
  }
  return s;
}

/**
 * Quem ganha quando dois horários se sobrepõem (sistema lento: as marcas
 * ficam juntas): o da antecedência desenhada, depois o mais distante. No
 * `declutter` do OpenLayers, o desenhado antes tem a prioridade. As outras
 * feições não têm texto e vêm depois.
 */
export function ordemDosRotulos(a: FeatureLike, b: FeatureLike): number {
  const peso = (f: FeatureLike) => (f.get('papel') !== 'rotulo' ? -1 : (f.get('naTela') === true ? 1000 : 0) + Number(f.get('h') ?? 0));
  return peso(b) - peso(a);
}

function estilo(f: FeatureLike): Style | Style[] | undefined {
  switch (f.get('papel') as Papel | undefined) {
    case 'faixa':
      return FAIXAS[Math.min(FAIXAS.length - 1, Number(f.get('nivel') ?? 0))];
    case 'borda':
      return BORDA;
    case 'marca':
      return f.get('cheia') === true || f.get('naTela') === true ? MARCA_CHEIA : MARCA_MEIA;
    case 'elipse':
      return ELIPSE;
    case 'origem':
      return ORIGEM;
    case 'ponto':
      return f.get('naTela') === true ? PONTO_NA_TELA : f.get('cheia') === true ? PONTO_CHEIO : PONTO_MEIO;
    case 'rotulo':
      return estiloDoRotulo(f);
    default:
      return undefined;
  }
}

const sateliteLigado = () => useGoesStore.getState().layers.some((l) => l.visible);

/** A previsão do arquivo leva o sistema mais rápido que o plausível (erro de associação): sem cone. */
function implausivel(p: CentrosPrevistos): boolean {
  const [a, b] = [p.passos[0], p.passos[p.passos.length - 1]];
  if (!a || !b || b.h <= a.h) return false;
  return (Math.hypot(b.centro[0] - a.centro[0], b.centro[1] - a.centro[1]) / (b.h - a.h)) * 0.06 > VELOCIDADE_MAXIMA_KMH;
}

/** A camada do cone: refeita quando a seleção, o quadro, a antecedência ou o loop mudam. */
export class CamadaTrajetoria {
  readonly layer: VectorLayer<VectorSource>;
  private readonly source = new VectorSource();
  private readonly paradas: (() => void)[] = [];
  private assinatura = '';
  private readonly celular: MediaQueryList | null;

  constructor(private readonly map: OlMap) {
    this.layer = new VectorLayer({
      source: this.source,
      style: estilo,
      zIndex: Z.TRACKING_TRAJETORIA,
      className: 'ol-layer amaview-trajetoria',
      // Os horários não se atropelam; grupo próprio, para não esconder (nem ser escondido por) outros rótulos.
      declutter: 'amaview-trajetoria',
      renderOrder: ordemDosRotulos,
      properties: { id: 'trajetoria', title: 'Cone de incerteza do sistema selecionado' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    map.addLayer(this.layer);
    this.celular = typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(CELULAR) : null;
    const atualizar = () => this.atualizar();
    this.celular?.addEventListener('change', atualizar);
    this.paradas.push(
      () => this.celular?.removeEventListener('change', atualizar),
      useTrackingStore.subscribe(atualizar),
      subscribeSelector(useTimeStore, (s) => shownTimeOf(s), atualizar),
      subscribeSelector(useLoopStore, (s) => s.playing, atualizar),
      subscribeSelector(useLoopStore, (s) => s.exportLocked, atualizar),
      subscribeSelector(useGoesStore, (s) => s.layers.some((l) => l.visible), atualizar),
      useConeStore.subscribe(atualizar),
    );
    this.atualizar();
  }

  /** A extensão do cone na tela (para a ficha desviar dele), ou null sem cone. */
  extensaoDoCone(): Extent | null {
    const e = createEmpty();
    for (const f of this.source.getFeatures()) if (f.get('papel') === 'borda') extend(e, f.getGeometry()!.getExtent());
    return isEmpty(e) ? null : e;
  }

  private atualizar(): void {
    const s = useTrackingStore.getState();
    const satelite = sateliteLigado();
    // Desligado pelo botão da ficha (`coneLigado.ts`), nada.
    const chave = satelite && useConeStore.getState().ligado ? sistemaDaSelecao(s.selecao, s.indice?.limiares ?? []) : null;
    const t = shownTimeOf(useTimeStore.getState());
    const loop = useLoopStore.getState();
    // Parado, o arquivo da previsão (o mesmo do contorno previsto); tocando ou exportando — quando ele não é
    // baixado —, ou enquanto não chega, o vetor do quadro. Assim o cone acompanha o sistema na animação.
    const parado = !loop.playing && !loop.exportLocked;
    const prevista = true;
    const naTela = previsaoDesenhada(s, satelite) || previsaoDoSelecionado(s, satelite);
    const compacto = !!this.celular?.matches;
    // Refaz só quando algo que o desenha mudou (o store do rastreio muda por muitos motivos).
    const assinatura = [chave, t, s.versaoDoRegistro, parado, naTela, compacto].join(' ');
    if (assinatura === this.assinatura) return;
    this.assinatura = assinatura;
    this.source.clear(true);
    if (chave === null) {
      this.source.changed();
      return;
    }
    const doArquivo = parado ? previsaoDoSistema(chave, t) : null;
    const previsao = doArquivo?.passos.length ? (implausivel(doArquivo) ? null : doArquivo) : previsaoPeloVetor(chave, t, s.indice?.antecedencias ?? []);
    const feicoes = feicoesDaTrajetoria({ ancora: ancoraDoSistema(chave, t), previsao, prevista, naTela, compacto });
    if (feicoes.length) this.source.addFeatures(feicoes);
    else this.source.changed();
  }

  dispose(): void {
    this.paradas.forEach((p) => p());
    this.source.clear(true);
    this.map.removeLayer(this.layer);
  }
}
